import React, { useState, useEffect } from 'react';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../listing/v2';
import {
  EditAction,
  CopyAction,
  CreateAction
} from '../listing/v2/ListingAction';
import { Badge } from '../../components';
import {
  FileText,
  ShieldAlert,
  Activity,
  CheckCircle,
  XCircle,
  Copy,
  Lock,
  PenTool,
  Info
} from 'lucide-react';
import clubDocumentApi from '../../services/clubDocumentApi';

const DocumentTemplateList = ({ club_id, onSelectTemplate, showActions = false, isAdmin = false, onEditTemplate, onCreateTemplate }) => {
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, [club_id]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);

      if (club_id === 'platform') {
        const platformResponse = await clubDocumentApi.getTemplates();
        const templates = platformResponse.data?.templates || platformResponse.templates || [];
        setAllTemplates(templates.map(t => ({
          ...t,
          source_scope: 'platform',
          is_inherited: false,
          is_editable: true
        })));
      } else {
        const response = await clubDocumentApi.getTemplates();
        const templates = response.data?.templates || response.templates || [];
        setAllTemplates(templates);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load document templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTemplate = async (platformTemplateId) => {
    try {
      await clubDocumentApi.copyPlatformTemplate(platformTemplateId);
      await fetchTemplates();
    } catch (err) {
      console.error('Error copying template:', err);
    }
  };

  const getAcceptanceModeLabel = (mode) => {
    const modes = {
      accept: 'Digital Signature',
      acknowledge: 'Acknowledgment'
    };
    return modes[mode] || mode;
  };

  const getCategoryLabel = (category) => {
    const categories = {
      waiver: 'Waiver',
      medical: 'Medical',
      registration: 'Registration',
      emergency_contact: 'Emergency Contact',
      concussion: 'Concussion',
      insurance: 'Insurance',
      public_profile_display: 'Public Info Sharing',
      other: 'Other'
    };
    return categories[category] || category?.replace(/_/g, ' ');
  };

  const filteredTemplates = allTemplates.filter(t => {
    if (showActiveOnly && !t.active) return false;
    if (searchValue) {
      const term = searchValue.toLowerCase();
      if (!t.title.toLowerCase().includes(term) &&
        !(t.description && t.description.toLowerCase().includes(term))) {
        return false;
      }
    }
    return true;
  });

  const platformTemplates = filteredTemplates.filter(t => t.source_scope === 'platform');
  const clubTemplates = filteredTemplates.filter(t => t.source_scope === 'club');
  const branchTemplates = filteredTemplates.filter(t => t.source_scope === 'branch');

  const filterBar = (
    <ListingFilter
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      filters={[
        {
          name: 'activeOnly',
          label: 'Status',
          type: 'select',
          options: [
            { label: 'All Templates', value: 'all' },
            { label: 'Active Only', value: 'active' }
          ]
        }
      ]}
      activeFilters={{ activeOnly: showActiveOnly ? 'active' : 'all' }}
      onFilterChange={(name, val) => setShowActiveOnly(val === 'active')}
      onClearFilters={() => {
        setSearchValue('');
        setShowActiveOnly(false);
      }}
    />
  );

  const renderTemplate = (template) => {
    const isAlreadyCopied = club_id !== 'platform' && allTemplates.some(
      ct => ct.parent_template_id === template.id && ct.source_scope === 'club'
    );

    return (
      <ListingItem
        key={template.id}
        title={template.title}
        subtitle={template.description}
        icon={template.category === 'waiver' ? <ShieldAlert size={32} /> : <FileText size={32} />}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            {template.is_editable && isAdmin && onEditTemplate && (
              <EditAction size="xs" onClick={() => onEditTemplate(template)} title="Edit Template" />
            )}
            {template.source_scope === 'platform' && club_id !== 'platform' && !isAlreadyCopied && isAdmin && (
              <CopyAction size="xs" label="Copy to Club" onClick={() => handleCopyTemplate(template.id)} title="Copy to Club" />
            )}
          </div>
        }
      >
        <div style={{ display: 'flex', gap: '2rem', marginTop: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.9rem', color: 'var(--color-text-tertiary)' }}>
            <Info size={16} style={{ opacity: 0.5 }} /> {getCategoryLabel(template.category)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.9rem', color: 'var(--color-text-tertiary)' }}>
            <PenTool size={16} style={{ opacity: 0.5 }} /> {getAcceptanceModeLabel(template.acceptance_mode)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: 'auto' }}>
            {template.active ? (
              <Badge variant="success" size="sm">ACTIVE</Badge>
            ) : (
              <Badge variant="warning" size="sm">INACTIVE</Badge>
            )}
            {template.is_inherited && (
              <Badge variant="outline" size="sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Lock size={12} /> INHERITED
              </Badge>
            )}
            {isAlreadyCopied && (
              <Badge variant="success" size="sm">COPIED</Badge>
            )}
          </div>
        </div>
      </ListingItem>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
      {branchTemplates.length > 0 && (
        <Listing
          title="Branch Infrastructures"
          items={branchTemplates}
          renderItem={renderTemplate}
          variant="list"
        />
      )}

      {club_id === 'platform' && (
        <Listing
          title="Platform Templates"
          items={platformTemplates}
          loading={loading}
          filters={filterBar}
          renderItem={renderTemplate}
          emptyTitle="No platform templates identified"
          emptyDescription="Configure your first platform template to begin."
          variant="list"
        />
      )}

      {club_id !== 'platform' && clubTemplates.length > 0 && (
        <Listing
          title="Club Infrastructure"
          items={clubTemplates}
          loading={loading}
          renderItem={renderTemplate}
          emptyTitle="No custom frameworks identified"
          emptyDescription="Configure your first organizational template to begin."
          variant="list"
        />
      )}

      {club_id !== 'platform' && platformTemplates.length > 0 && (
        <Listing
          title="Strategic Library"
          description="Established patterns provided by the platform core."
          items={platformTemplates}
          filters={filterBar}
          renderItem={renderTemplate}
          variant="list"
        />
      )}
    </div>
  );
};

export default DocumentTemplateList;